import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { useSupabase } from '../contexts/SupabaseContext';
import { useBusiness } from '../contexts/BusinessContext';
import { getPendingDocumentVerifications, type PendingDocumentVerification } from '../lib/documents';
import { createBusinessCache } from '../lib/controlCentre/actionsCache';
import type { CrmRequiredActionItem } from '../lib/crm/requiredActions';

const verificationsCache = createBusinessCache<PendingDocumentVerification[]>({
  persistKey: 'document-verifications',
});

function buildDocumentVerificationActions(rows: PendingDocumentVerification[]): CrmRequiredActionItem[] {
  return rows.map((row) => {
    const submittedAt = row.submitted_at;
    const submitted = submittedAt ? new Date(submittedAt) : null;
    const submittedMs = submitted ? submitted.getTime() : 0;
    return {
      kind: 'document_verification' as const,
      documentSubmissionId: row.submission_id,
      title: row.title?.trim() || 'Document',
      customerName: row.submitted_by_name?.trim() || 'Team member',
      subtitle: submitted && !Number.isNaN(submittedMs)
        ? `Awaiting sign-off · ${formatDistanceToNow(submitted, { addSuffix: true })}`
        : 'Awaiting sign-off',
      // Oldest pending first (more urgent) within the kind; flatten re-sorts globally.
      sortOrder: submittedMs,
      createdAt: submittedAt || new Date().toISOString(),
    };
  });
}

/** Completed documents awaiting the current user's verification sign-off. */
export function useDocumentVerificationRequiredActions(enabled = true) {
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id ?? '';

  const [rows, setRows] = useState<PendingDocumentVerification[]>(
    () => verificationsCache.get(businessId) ?? [],
  );
  const [isLoading, setIsLoading] = useState(() => !verificationsCache.has(businessId));
  const [error, setError] = useState<string | null>(null);

  const fetchRows = useCallback(async () => {
    if (!businessId) return;
    const data = await getPendingDocumentVerifications(businessId);
    setRows(data);
    verificationsCache.set(businessId, data);
  }, [businessId]);

  const load = useCallback(async () => {
    if (!enabled || !businessId) {
      setRows([]);
      setIsLoading(false);
      return;
    }
    if (verificationsCache.has(businessId)) {
      setRows(verificationsCache.get(businessId) ?? []);
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
    setError(null);
    try {
      await fetchRows();
    } catch (err) {
      console.error('Error loading document verification actions:', err);
      setError('Failed to load document sign-offs.');
    } finally {
      setIsLoading(false);
    }
  }, [enabled, businessId, fetchRows]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      setRows([]);
      return;
    }
    void load();
  }, [load, enabled]);

  const refreshDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!enabled || !businessId || !supabase || isLoading) return;
    const channel = supabase
      .channel(`document-verification-actions-${businessId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'document_submissions', filter: `business_id=eq.${businessId}` },
        () => {
          if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
          refreshDebounceRef.current = setTimeout(() => { void fetchRows(); }, 400);
        },
      )
      .subscribe();
    return () => {
      if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
      void supabase.removeChannel(channel);
    };
  }, [enabled, businessId, supabase, isLoading, fetchRows]);

  const documentVerificationActions = useMemo(() => buildDocumentVerificationActions(rows), [rows]);

  return { documentVerificationActions, isLoading, error, refresh: load };
}
